import React, { useState } from 'react';
import {
  X,
  ExternalLink,
  Calendar,
  Tag,
  ShieldCheck,
  AlertCircle,
  FileText,
  Bot,
  User,
  CheckCircle2,
  XCircle,
  Clock,
  Sparkles,
} from 'lucide-react';

const REGULATORY_STAGE_LABELS = {
  DRAFT_PROPOSAL: { label: 'Proposition / Projet', badgeClass: 'info' },
  PARLIAMENT_BILL: { label: 'Projet de Loi en Débat', badgeClass: 'warning' },
  LAW_PASSED: { label: 'Loi Adoptée (ARP)', badgeClass: 'primary' },
  DECREE_PUBLISHED: { label: 'Décret d’Application Publié', badgeClass: 'primary' },
  IN_FORCE_ACTIVE: { label: 'En Vigueur (Applicable)', badgeClass: 'success' },
  AMENDED_SUPERSEDED: { label: 'Modifié / Remplacé', badgeClass: 'secondary' },
  REPEALED_EXPIRED: { label: 'Abrogé / Expiré', badgeClass: 'danger' },
};

export default function ArticleDetailModal({
  isOpen,
  onClose,
  article,
  onApprove,
  onReject,
  onEdit,
}) {
  const [activeTab, setActiveTab] = useState('preview'); // 'preview' | 'metadata' | 'source'

  if (!isOpen || !article) return null;

  const regStage = REGULATORY_STAGE_LABELS[article.tunisianContext?.regulatoryStage] || {
    label: article.tunisianContext?.regulatoryStage || 'Non spécifié',
    badgeClass: 'neutral',
  };

  const isPending = article.validationStatus === 'PENDING_REVIEW';

  const formatDate = (d) => {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  };

  return (
    <div className="an-modal-backdrop animate-fade-in" onClick={onClose}>
      <div className="an-modal-card an-modal-large" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="an-modal-header">
          <div className="an-modal-title">
            <span className="an-article-code-pill mr-2">{article.articleCode}</span>
            <span className="text-truncate">{article.title}</span>
          </div>
          <button className="an-modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* Sub-header info badges */}
        <div className="an-modal-subbar">
          <div className="an-subbar-badges">
            <span className={`an-badge an-badge-${regStage.badgeClass}`}>
              {regStage.label}
            </span>

            {article.origin === 'EXTERNAL_AGENT' ? (
              <span className="an-badge an-badge-agent">
                <Bot size={13} className="mr-1" />
                Collecté par Agent IA
              </span>
            ) : (
              <span className="an-badge an-badge-neutral">
                <User size={13} className="mr-1" />
                Rédaction Manuelle
              </span>
            )}

            <span className={`an-badge an-badge-val-${article.validationStatus?.toLowerCase()}`}>
              {article.validationStatus === 'APPROVED' && <CheckCircle2 size={13} className="mr-1" />}
              {article.validationStatus === 'REJECTED' && <XCircle size={13} className="mr-1" />}
              {article.validationStatus === 'PENDING_REVIEW' && <Clock size={13} className="mr-1" />}
              {article.validationStatus}
            </span>

            {article.aiMetadata?.confidenceScore !== undefined && (
              <span className="an-badge an-badge-score">
                <Sparkles size={13} className="mr-1" />
                Score IA : {article.aiMetadata.confidenceScore}%
              </span>
            )}
          </div>

          {/* Tab Navigation */}
          <div className="an-modal-nav-tabs">
            <button
              type="button"
              className={`an-modal-nav-tab ${activeTab === 'preview' ? 'active' : ''}`}
              onClick={() => setActiveTab('preview')}
            >
              Aperçu de l'Article
            </button>
            <button
              type="button"
              className={`an-modal-nav-tab ${activeTab === 'metadata' ? 'active' : ''}`}
              onClick={() => setActiveTab('metadata')}
            >
              Contexte Tunisien & Métadonnées
            </button>
            {article.sourceUrl && (
              <button
                type="button"
                className={`an-modal-nav-tab ${activeTab === 'source' ? 'active' : ''}`}
                onClick={() => setActiveTab('source')}
              >
                Source & Données Brutes
              </button>
            )}
          </div>
        </div>

        {/* Modal Body */}
        <div className="an-modal-body">
          {activeTab === 'preview' && (
            <div className="an-detail-preview">
              {article.coverImage?.url && (
                <div className="an-detail-cover">
                  <img src={article.coverImage.url} alt={article.coverImage.caption || article.title} />
                  {article.coverImage.caption && (
                    <div className="an-cover-caption">{article.coverImage.caption}</div>
                  )}
                </div>
              )}

              <h1 className="an-detail-title">{article.title}</h1>

              {article.excerpt && (
                <div className="an-detail-excerpt">
                  <strong>Résumé :</strong> {article.excerpt}
                </div>
              )}

              <div
                className="an-detail-content"
                dangerouslySetInnerHTML={{
                  __html: article.contentHtml || '<p><em>Aucun contenu HTML</em></p>',
                }}
              />
            </div>
          )}

          {activeTab === 'metadata' && (
            <div className="an-detail-metadata-grid">
              <div className="an-meta-card">
                <h4>Catégorie & Thématiques</h4>
                <div className="an-meta-row">
                  <span className="an-meta-label">Catégorie principale :</span>
                  <span className="an-meta-value">
                    {article.primaryCategoryId?.name || 'Non renseignée'}
                  </span>
                </div>
                <div className="an-meta-row">
                  <span className="an-meta-label">Domaines énergétiques :</span>
                  <div className="an-tag-cloud">
                    {(article.tunisianDomains || []).map((d) => (
                      <span key={d} className="an-pill-tag">
                        {d}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              <div className="an-meta-card">
                <h4>Contexte Légal & Réglementaire Tunisien</h4>
                <div className="an-meta-row">
                  <span className="an-meta-label">Stade réglementaire :</span>
                  <span className="an-meta-value font-bold">{regStage.label}</span>
                </div>
                <div className="an-meta-row">
                  <span className="an-meta-label">Références officielles (JORT) :</span>
                  <span className="an-meta-value">
                    {article.tunisianContext?.officialJournalReferences || 'Aucune référence JORT'}
                  </span>
                </div>
                {article.tunisianContext?.effectiveDate && (
                  <div className="an-meta-row">
                    <span className="an-meta-label">Date d'effet / application :</span>
                    <span className="an-meta-value">
                      {formatDate(article.tunisianContext.effectiveDate)}
                    </span>
                  </div>
                )}
              </div>

              <div className="an-meta-card">
                <h4>Horodatage & Auteur</h4>
                <div className="an-meta-row">
                  <span className="an-meta-label">Date de création :</span>
                  <span className="an-meta-value">{formatDate(article.createdAt)}</span>
                </div>
                <div className="an-meta-row">
                  <span className="an-meta-label">Date de publication :</span>
                  <span className="an-meta-value">
                    {article.publishedAt ? formatDate(article.publishedAt) : 'Non publié'}
                  </span>
                </div>
                <div className="an-meta-row">
                  <span className="an-meta-label">Temps de lecture estimé :</span>
                  <span className="an-meta-value">{article.readingTimeMinutes || 1} min</span>
                </div>
              </div>

              {article.validationStatus === 'REJECTED' && (
                <div className="an-meta-card border-danger">
                  <h4 className="text-danger">Détails du Rejet</h4>
                  <p className="an-rejection-quote">"{article.rejectionReason}"</p>
                  <small className="text-muted">
                    Rejeté le {formatDate(article.validatedAt)}
                  </small>
                </div>
              )}
            </div>
          )}

          {activeTab === 'source' && (
            <div className="an-detail-source">
              <div className="an-source-url-banner">
                <span>Source d'origine :</span>
                <a
                  href={article.sourceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="an-source-link"
                >
                  {article.sourceUrl}
                  <ExternalLink size={14} className="ml-1" />
                </a>
              </div>

              <div className="an-source-text-box">
                <h4>Texte brut collecté</h4>
                <pre>{article.contentRaw || 'Aucun texte brut archivé.'}</pre>
              </div>
            </div>
          )}
        </div>

        {/* Modal Footer Actions */}
        <div className="an-modal-footer">
          <button type="button" className="an-btn-secondary" onClick={onClose}>
            Fermer
          </button>

          {onEdit && (
            <button
              type="button"
              className="an-btn-primary outline"
              onClick={() => {
                onClose();
                onEdit(article);
              }}
            >
              Modifier le contenu
            </button>
          )}

          {isPending && onReject && (
            <button
              type="button"
              className="an-btn-danger"
              onClick={() => {
                onClose();
                onReject(article);
              }}
            >
              Rejeter
            </button>
          )}

          {isPending && onApprove && (
            <button
              type="button"
              className="an-btn-success"
              onClick={() => {
                onClose();
                onApprove(article);
              }}
            >
              Approuver la proposition
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

